import { isStaff, isSuperadmin, roleLabelKey } from "../roles";
import AccountSwitcher from "./AccountSwitcher";
import {
  Activity,
  BookOpen,
  ClipboardCheck,
  FileClock,
  FileText,
  Gauge,
  Images,
  LogOut,
  Menu,
  Plus,
  ReceiptText,
  Search,
  PieChart,
  Settings2,
  Users2,
  X,
  Handshake,
  CalendarCheck,
  CalendarHeart,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import type { AccountSummary, SessionUser } from "../types";
import { formatCredits, monthLabel } from "../utils";
import { useT } from "../i18n";
import Amount from "./Amount";
import ThemeToggle from "./ThemeToggle";
import LanguageToggle from "./LanguageToggle";

type NavItem = {
  href: string;
  label: string;
  icon: typeof Gauge;
  adminOnly?: boolean;
  superadminOnly?: boolean;
};

const navItems: NavItem[] = [
  { href: "/", label: "nav.overview", icon: Gauge },
  { href: "/requests", label: "nav.requests", icon: ClipboardCheck },
  { href: "/work", label: "nav.work", icon: Images },
  { href: "/search", label: "nav.search", icon: Search },
  { href: "/services", label: "nav.catalogue", icon: BookOpen },
  { href: "/statement", label: "nav.statement", icon: FileText },
  { href: "/ledger", label: "nav.ledger", icon: ReceiptText },
  { href: "/month-review", label: "nav.monthReview", icon: CalendarHeart },
  { href: "/relationship", label: "nav.relationship", icon: Handshake },
  { href: "/review", label: "nav.review", icon: CalendarCheck },
  { href: "/operations", label: "Prismic operations", icon: Settings2, adminOnly: true },
  { href: "/activity", label: "Activity log", icon: Activity, adminOnly: true },
  { href: "/portfolio", label: "Portfolio", icon: PieChart, superadminOnly: true },
  { href: "/administration", label: "Clients & people", icon: Users2, superadminOnly: true },
];

const pageTitles: Record<string, { title: string; eyebrow: string; staffOnly?: boolean }> = {
  "/": { title: "nav.overview", eyebrow: "" },   // filled from the active account below
  "/requests": { title: "requests.title", eyebrow: "requests.eyebrow" },
  "/request/new": { title: "shell.requestService", eyebrow: "form.newRequest" },
  "/work": { title: "work.title", eyebrow: "work.eyebrow" },
  "/search": { title: "search.title", eyebrow: "search.eyebrow" },
  "/services": { title: "nav.catalogue", eyebrow: "nav.catalogueEyebrow" },
  "/statement": { title: "statement.title", eyebrow: "statement.eyebrow" },
  "/ledger": { title: "ledger.title", eyebrow: "ledger.eyebrow" },
  // Registered here as well as in navItems. The map is EXACT-MATCH, so a route
  // that misses it falls through to the overview title and the header lies about
  // which page you are on (context.md trap 23).
  "/month-review": { title: "nav.monthReview", eyebrow: "nav.monthReviewEyebrow" },
  "/relationship": { title: "nav.relationship", eyebrow: "nav.relationshipEyebrow" },
  "/review": { title: "nav.review", eyebrow: "nav.reviewEyebrow" },
  // PRISMIC-ONLY, English on purpose — our team uses these, not the client, and
  // translating them would be volume without a reader. `staffOnly` says so in the
  // code so the leak guard can honour the decision instead of a hard-coded list.
  "/operations": { title: "Prismic operations", eyebrow: "Internal account controls", staffOnly: true },
  "/activity": { title: "Activity log", eyebrow: "Account audit trail", staffOnly: true },
  "/portfolio": { title: "Portfolio", eyebrow: "Every client, Prismic view", staffOnly: true },
  "/administration": { title: "Clients & people", eyebrow: "Super admin", staffOnly: true },
};

export default function AppShell({
  user,
  month,
  months,
  available,
  total,
  onMonthChange,
  onNewRequest,
  onLogout,
  children,
  accountName,
  accounts,
  onSwitchAccount,
}: {
  user: SessionUser;
  month: string;
  months: string[];
  available: number;
  total: number;
  onMonthChange: (month: string) => void;
  onNewRequest: () => void;
  onLogout: () => void;
  children: ReactNode;
  accountName: string;
  /** Empty for clients — the server never sends them the list. */
  accounts: AccountSummary[];
  onSwitchAccount: (accountId: string) => void;
}) {
  const t = useT();
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Exact-match lookup only, so /delivery/<id> fell through to the overview title
  // and the header said "Credit overview" above a delivered film.
  const current = pageTitles[location]
    || (location.startsWith("/delivery/") ? { title: "delivery.headerTitle", eyebrow: "delivery.headerEyebrow" } : null)
    || pageTitles["/"];

  useEffect(() => setMobileOpen(false), [location]);

  // Hiding a link is not a permission — the server guards these routes. This just
  // keeps the nav honest about what the person can actually do.
  const navigation = navItems.filter((item) =>
    (!item.adminOnly || isStaff(user.role)) && (!item.superadminOnly || isSuperadmin(user.role)),
  );
  const sidebar = (
    <>
      {/* The client's own initial and name, not a hardcoded Z — and, for staff
          with more than one client, the control that changes it. The separate
          <select> that used to sit below this is gone: two controls naming the
          same thing is a question the reader has to answer first. */}
      <div className="sidebar-brand">
        <AccountSwitcher
          accounts={accounts}
          activeId={user.accountId}
          accountName={accountName}
          onSwitch={onSwitchAccount}
        />
        <button className="sidebar-close" onClick={() => setMobileOpen(false)} title={t("shell.closeNav")} aria-label={t("shell.closeNav")}>
          <X size={20} />
        </button>
      </div>

      <nav className="sidebar-nav" aria-label={t("shell.mainNav")}>
        {navigation.map((item) => {
          const Icon = item.icon;
          const active = item.href === "/" ? location === "/" : location.startsWith(item.href);
          return (
            <Link key={item.href} href={item.href} className={`nav-link ${active ? "active" : ""}`}>
              <Icon size={18} />
              <span>{t(item.label)}</span>
            </Link>
          );
        })}
      </nav>

      {/* Above the account rather than in the top bar: it is a preference, set once,
          and it does not belong next to the month and the balance a reader changes
          every day. */}
      <ThemeToggle />
      <LanguageToggle />

      <div className="sidebar-account">
        <div className="account-avatar">{user.displayName.charAt(0).toUpperCase()}</div>
        <div className="account-copy">
          <strong>{user.displayName}</strong>
          <span>{t(roleLabelKey(user.role))}</span>
        </div>
        <button className="sidebar-logout" onClick={onLogout} title={t("shell.signOut")} aria-label={t("shell.signOut")}>
          <LogOut size={18} />
        </button>
      </div>
    </>
  );

  return (
    <div className="app-shell">
      <aside className="sidebar">{sidebar}</aside>
      {mobileOpen && <button className="mobile-overlay" onClick={() => setMobileOpen(false)} aria-label={t("shell.closeNav")} />}
      <aside className={`mobile-sidebar ${mobileOpen ? "open" : ""}`}>{sidebar}</aside>

      <main className="app-main">
        <header className="topbar">
          <div className="topbar-title">
            <button className="menu-button" onClick={() => setMobileOpen(true)} title={t("shell.openNav")} aria-label={t("shell.openNav")}>
              <Menu size={21} />
            </button>
            <div>
              {/* Falls back to the ACTIVE client, never a fixed name. This read
                  "Zain Kuwait" while a staff member was working in NBK — the wrong
                  client's name above the right client's numbers. */}
              <span>{current.eyebrow ? t(current.eyebrow) : accountName}</span>
              <h1>{t(current.title)}</h1>
            </div>
          </div>

          <div className="topbar-actions">
            <label className="month-control">
              <FileClock size={17} />
              <span className="sr-only">{t("shell.reportingMonth")}</span>
              <select value={month} onChange={(event) => onMonthChange(event.target.value)}>
                {months.map((item) => <option key={item} value={item}>{monthLabel(item)}</option>)}
              </select>
            </label>
            <div className="header-balance" title={t("aria.poolTotal", { credits: formatCredits(total) })}>
              <span>{t("shell.available")}</span>
              <strong><Amount value={available} unit={t("common.cr")} /></strong>
            </div>
            {/* Hidden on the request page itself. It is not merely redundant there:
                pressing it navigates to /request/new WITHOUT the ?from= or ?service=
                that filled the form, so a copied brief would vanish with no warning
                and no way back to it. */}
            {!location.startsWith("/request/new") && (
              <button className="button button-primary topbar-request" onClick={onNewRequest}>
                <Plus size={18} />
                <span>{t("shell.requestService")}</span>
              </button>
            )}
          </div>
        </header>
        <div className="page-content">{children}</div>
      </main>
    </div>
  );
}
